import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { YearChips, yearsToSeasonParam, yearsLabel } from '../components/YearChips';
import { useCollapseOnScroll } from '../components/useCollapseOnScroll';
import { FilterSheet } from '../components/FilterSheet';

function BananaCard({ category }) {
  const { title, caption, tagline, data, unit, suffix, emoji } = category;
  if (!data || data.length === 0) return null;

  return (
    <div style={{
      background: 'white', borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 16,
      border: '2px solid #fef3c7',
    }}>
      <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', padding: '12px 16px' }}>
        <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem', color: '#92400e' }}>
          {emoji} {title}
        </div>
        <div style={{ fontSize: '0.72rem', color: '#b45309', marginTop: 2, fontStyle: 'italic' }}>
          "{caption}"
        </div>
        {tagline && (
          <div style={{ fontSize: '0.68rem', color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
            💪 {tagline}
          </div>
        )}
      </div>

      {data.map((row, i) => (
        <div key={row.id || row.display_name || i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px',
          borderBottom: i < data.length - 1 ? '1px solid #f9fafb' : 'none',
          background: i === 0 ? '#fffbeb' : 'white',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: i === 0 ? '#fde68a' : '#f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: i < 3 ? '1.1rem' : '0.75rem', fontWeight: 700,
            border: i === 0 ? '2px solid #f59e0b' : '1px solid #e5e7eb',
          }}>
            {i < 3 ? '🍌' : i + 1}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1E3A6E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.display_name}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>
              {row.ucc_id}
              {row.wides !== undefined ? ` · ${row.wides}W ${row.noballs}NB` : ''}
              {row.runs !== undefined && row.balls !== undefined ? ` · ${row.runs}r off ${row.balls}b` : ''}
              {row.present !== undefined ? ` · ${row.present}/${row.total} matches` : ''}
              {row.fine_count !== undefined ? ` · ${row.fine_count} fines` : ''}
              {row.total_overs !== undefined ? ` · ${parseFloat(row.total_overs||0).toFixed(1)} ov` : ''}
              {row.notes ? ` · ${row.notes}` : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.25rem', color: i === 0 ? '#d97706' : '#374151' }}>
              {suffix === '₹' ? `₹${parseInt(row.value||0).toLocaleString('en-IN')}` : `${row.value}${suffix}`}
            </div>
            <div style={{ fontSize: '0.6rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{unit}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BananaPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Session 16 — multi-year state
  const [yearsState, setYearsState] = useState({ mode: 'single', value: 2026 });
  const season = yearsToSeasonParam(yearsState);
  const label = yearsLabel(yearsState);
  const AVAILABLE_YEARS = [2026,2025,2024,2023,2022,2021,2020,2019,2018,2017,2016,2015,2014,2013];
  // Session 16 Pass 2 — collapse-on-scroll + filter modal
  const collapsed = useCollapseOnScroll(120);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/leaderboards/banana?season=${season}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [season]);

  const categories = data ? [
    { ...data.ducks,       emoji: '🦆', tagline: 'Every master was once a duck. Your century is coming!' },
    { ...data.worstSR,     emoji: '🐢', tagline: 'Slow is fine — but there is always room to accelerate!' },
    { ...data.runOuts,     emoji: '🏃', tagline: 'Quick running is great — just make sure partner agrees first!' },
    { ...data.economy,     emoji: '🚿', tagline: 'The best bowlers were once expensive. Tighten up — you have got this!' },
    { ...data.extras,      emoji: '🎁', tagline: 'Precision is a skill that comes with practice. You are getting there!' },
    { ...data.dropCatches, emoji: '🧤', tagline: 'Soft hands and sharp eyes — next one is yours for sure!' },
    { ...data.attendance,  emoji: '👻', tagline: 'More matches = more memories. We miss you out there!' },
    { ...data.fines,       emoji: '💸', tagline: 'The club thanks you! Now let us work on keeping it in your pocket!' },
  ].filter(c => c.data && c.data.length > 0) : [];

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Sticky Header — collapses on scroll */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50, overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #1E3A6E, #14532d)',
      }}>
        {collapsed ? (
          /* COLLAPSED — compact bar */
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', color: 'white' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '0.95rem', color: '#F5C518', lineHeight: 1.1 }}>
                🍌 Banana Awards
              </div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', marginTop: 2,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label}
              </div>
            </div>
            <button onClick={() => setFilterOpen(true)}
              style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 14, padding: '5px 12px', color: 'white',
                fontFamily: 'Rajdhani,sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>
              ↕ Change
            </button>
          </div>
        ) : (
          /* EXPANDED */
          <div style={{ padding: '20px 16px 20px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: -10, right: -10, fontSize: '6rem', opacity: 0.07 }}>🍌</div>
            <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.5rem', color: '#F5C518' }}>
              🍌 Banana Awards
            </div>
            <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
              {label} · The stats nobody asked for
            </div>
            <div style={{ marginTop: 10 }}>
              <YearChips years={yearsState} availableYears={AVAILABLE_YEARS} onChange={setYearsState} dark />
            </div>
          </div>
        )}
      </div>

      {/* Filter modal */}
      <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} title="Filters">
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>SEASON</div>
        <YearChips years={yearsState} availableYears={AVAILABLE_YEARS} onChange={setYearsState} />
      </FilterSheet>

      <div style={{ padding: '16px 16px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>🍌</div>
            Loading…
          </div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No data yet — import scorecards first</div>
        ) : categories.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No data for {label} yet</div>
        ) : (
          categories.map((cat, i) => <BananaCard key={i} category={cat} />)
        )}
      </div>
    </div>
  );
}
